import { Badge, Box, Code, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import React from "react";
import {
  FiClock,
  FiExternalLink,
  FiHash,
  FiLink,
  FiNavigation,
  FiPlus,
  FiShuffle
} from "react-icons/fi";

interface LinkPreviewProps {
  platform: string;
  linkName?: string;
  adSetName?: string;
  adName?: string;
  destinationType: "single" | "ab-test" | "variant";
  destinationPath?: string;
  destinationVariant?: string;
  abTestPaths?: Array<{
    id: string;
    type: "path" | "variant";
    value: string;
    percentage: number;
  }>;
  abTestSchedule?: {
    enabled: boolean;
    startDate?: string;
    endDate?: string;
  };
  forceExternalBrowser?: boolean;
  customQueryParams?: string;
  generatedUrl: string;
  domain: string;
  customId: string;
  redirectUrl?: string;
}

export const LinkPreview: React.FC<LinkPreviewProps> = ({
  platform,
  linkName,
  adSetName,
  adName,
  destinationType,
  destinationPath,
  destinationVariant,
  abTestPaths,
  abTestSchedule,
  forceExternalBrowser,
  customQueryParams,
  domain,
  customId,
  redirectUrl: _redirectUrl
}) => {
  return (
    <Box bg="white" shadow="sm" rounded="lg" p={6}>
      <HStack mb={4}>
        <Icon as={FiLink} color="green.500" boxSize={5} />
        <Text fontWeight="semibold" fontSize="lg">
          Link Preview
        </Text>
      </HStack>

      <Box
        p={4}
        bg="gray.50"
        rounded="md"
        border="2px dashed"
        borderColor="gray.300"
      >
        {platform ? (
          <VStack align="stretch" gap={3}>
            <Text fontSize="sm" color="gray.600">
              Your tracking link will look like:
            </Text>
            <Code
              p={3}
              rounded="md"
              fontSize="sm"
              bg="gray.100"
              wordBreak="break-all"
            >
              https://{domain || "try.zown.ca"}/{customId || "[generated-id]"}
            </Code>
            <Box h="1px" bg="gray.200" my={2} />
            <VStack align="start" gap={1} fontSize="sm">
              <HStack>
                <Text color="gray.600">Platform:</Text>
                <Badge colorScheme="blue">{platform}</Badge>
              </HStack>
              {linkName && (
                <HStack>
                  <Text color="gray.600">Campaign:</Text>
                  <Text fontWeight="medium">{linkName}</Text>
                </HStack>
              )}
              {adSetName && (
                <HStack>
                  <Text color="gray.600">Ad Set:</Text>
                  <Text>{adSetName}</Text>
                </HStack>
              )}
              {adName && (
                <HStack>
                  <Text color="gray.600">Ad:</Text>
                  <Text>{adName}</Text>
                </HStack>
              )}
              <Box h="1px" bg="gray.200" my={2} />
              <Text fontSize="sm" color="gray.600">
                Destination:
              </Text>
              {destinationType === "single" ? (
                <HStack>
                  <Icon as={FiNavigation} color="green.500" boxSize={3} />
                  <Text fontSize="sm" wordBreak="break-all">
                    {destinationPath || "[not set]"}
                  </Text>
                </HStack>
              ) : destinationType === "ab-test" ? (
                <VStack align="start" gap={1}>
                  <HStack>
                    <Icon as={FiShuffle} color="blue.500" boxSize={3} />
                    <Text fontSize="xs" fontWeight="medium" color="blue.600">
                      A/B Test Active
                    </Text>
                    {abTestSchedule?.enabled && (
                      <Icon as={FiClock} color="blue.500" boxSize={3} />
                    )}
                  </HStack>
                  {abTestPaths?.map((path, index) => (
                    <HStack pl={4} key={path.id}>
                      <Badge
                        colorScheme={
                          ["green", "orange", "blue", "purple"][index % 4]
                        }
                        size="sm"
                      >
                        {path.percentage}%
                      </Badge>
                      <Text fontSize="xs">
                        {path.type === "variant" ? (
                          <Text as="span" fontFamily="mono">
                            #{path.value || "[variant]"}
                          </Text>
                        ) : (
                          path.value ||
                          `[path ${String.fromCharCode(65 + index)}]`
                        )}
                      </Text>
                    </HStack>
                  ))}
                  {abTestSchedule?.enabled && abTestSchedule.startDate && (
                    <Text fontSize="xs" color="gray.500" pl={4}>
                      Scheduled:{" "}
                      {new Date(abTestSchedule.startDate).toLocaleDateString()}
                    </Text>
                  )}
                </VStack>
              ) : (
                <HStack>
                  <Icon as={FiHash} color="purple.500" boxSize={3} />
                  <Text fontSize="sm" fontFamily="mono" wordBreak="break-all">
                    {destinationVariant || "[not set]"}
                  </Text>
                </HStack>
              )}
              {forceExternalBrowser && (
                <HStack>
                  <Icon as={FiExternalLink} color="purple.500" boxSize={3} />
                  <Text color="purple.600" fontSize="xs" fontWeight="medium">
                    External Browser Enabled
                  </Text>
                </HStack>
              )}
              {customQueryParams && (
                <HStack>
                  <Icon as={FiPlus} color="blue.500" boxSize={3} />
                  <Text color="blue.600" fontSize="xs" fontWeight="medium">
                    Custom Parameters:{" "}
                    {customQueryParams.split("&").filter((p) => p).length} added
                  </Text>
                </HStack>
              )}
            </VStack>
          </VStack>
        ) : (
          <Text fontSize="sm" color="gray.500" textAlign="center">
            Fill out the form to see your link preview
          </Text>
        )}
      </Box>
    </Box>
  );
};
